import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Auth } from "aws-amplify";
import { ArrowForwardIos, ArrowBackIos } from "@material-ui/icons";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  IconButton, Box,
} from "@material-ui/core";

import { display, spacing } from "@material-ui/system";
import _ from "lodash";
import "../../assets/css/order.css";
import ReportInterface from "../../integration/internal/ReportInterface";
import Loading from "../components/Loading";
import DialogContentText from "@material-ui/core/DialogContentText";
import { RadonUtil } from "../components/RadonUtil";
import { savePDF } from "@progress/kendo-react-pdf";
import { reformatRadonChartData } from "./util";
import { AuthUtil } from "../auth/AuthUtil";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { apiEndPoint } from "../../env";
import { CRITICAL_ACTION_TIMEOUT } from "../../constants/values";
import moment from "moment";
import ReportPreview, {
  PAGE_MAIN,
  PAGE_TEST_INFO,
  PAGE_CHART,
  PAGE_RADON_TABLE,
  PAGE_ADDITIONAL,
  PAGE_RECOMMENDATIONS,
  PAGE_UNCERTAINTY,
} from "./ReportPreview";
import ForPdf from "./ForPdf";
import { styles } from "./PageMain";
import { isCanadaReport } from "../../utils/reportCountry";
import { ensurePdfFonts } from "./pdfFontSetup";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(display);

function ReportDetails() {
  const location = useLocation();
  const [reportDetails, setReportDetails] = useState({});
  const [reportDuration, setReportDuration] = useState({
    start: null,
    end: null,
  });
  const [showNoData, setShowNoData] = useState(false);
  const [page, setPage] = useState(1);

  const pdfExportComponent = useRef(null);
  const history = useHistory();
  const [reportChartRadonValue, setReportChartRadonValue] = useState([]);

  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageAdditional, setPageAdditional] = React.useState(false);
  const [pageRecommendations, setPageRecommendations] = React.useState(false);
  const [pageUncertainty, setPageUncertainty] = React.useState(false);
  const [lastPageIndex, setLastPageIndex] = React.useState(0);

  const skipFlag = location.state.skipFlag;
  const originalGenerated = location.state.originalGenerated;

  let res;

  useEffect(() => {
    ensurePdfFonts();
  }, []);

  useEffect(() => {
    const getReportDetails = async () => {
      if (!location.state) return;

      if (!location.state.serial_number) {
        // user redirected to report details from email link
        const userEmail = localStorage.getItem("0xaccountId");
        const queryParams = new URLSearchParams(location.search);

        const serialNumber = queryParams.get("serial_number");
        const measurementStart = queryParams.get("measurement_start");
        const measurementEnd = queryParams.get("measurement_end");
        const types = queryParams.get("types");

        history.replace(history.location.pathname, {
          email: userEmail,
          name: localStorage.getItem("0xuserId"),
          certNo: localStorage.getItem("0xcertNo"),
          serial_number: [serialNumber],
          duration: {
            start: measurementStart,
            end: measurementEnd,
            type: types,
          },
        });

        const duration = {
          measurement_start: measurementStart,
          measurement_end: measurementEnd,
          types,
        };

        let res;
        if (types === "48HR") {
          res = await ReportInterface.retrieve48HrReportDetails(
            userEmail,
            serialNumber,
          );
        }
        else {
          res = await ReportInterface.retrieveReportDetails(
            userEmail,
            serialNumber,
            duration
          );
        }

        if ((res && !res.data) || !res) {
          setReportDetails(null);
          setReportDuration({
            start: duration.measurement_start,
            end: duration.measurement_end,
            type: types,
          });
          setShowNoData(true);
        } else if (res && !_.isEqual(reportDetails, res.data)) {
          let retData = reformatRadonChartData(res.data, types, null);

          setReportChartRadonValue(retData);
          setReportDetails(res.data);
          setReportDuration({
            start: duration.measurement_start,
            end: duration.measurement_end,
            type: types,
          });
          setPageVisible(res);
        }
      } else {
        // user redirect from report list
        let durationSet;
        if (location.state.actionType === "VIEW") {
          durationSet = {
            measurement_start: location.state.duration.start,
            measurement_end: location.state.duration.end,
            types: location.state.duration.btnType,
          };
        } else {
          durationSet = RadonUtil.convert2DurationSet(location.state.duration);
        }

        let res;
        if (location.state.duration.type === "48HR") {
          res = await ReportInterface.retrieve48HrReportDetails(
            location.state.email,
            location.state.serial_number[0],
          );
        }
        else {
          res = await ReportInterface.retrieveReportDetails(
            location.state.email,
            location.state.serial_number[0],
            durationSet
          );
        }

        if ((res && !res.data) || !res) {
          setReportDetails(null);
          setReportDuration({
            start: durationSet.measurement_start,
            end: durationSet.measurement_end,
            type: location.state.duration.type,
          });
          setShowNoData(true);
        } else if (res && !_.isEqual(reportDetails, res.data)) {
          let retData = reformatRadonChartData(
            res.data,
            RadonUtil.getBtnType(location.state.duration.type),
            location.state.duration.type
          );
          setReportChartRadonValue(retData);
          setReportDetails(res.data);
          setReportDuration({
            start: durationSet.measurement_start,
            end: durationSet.measurement_end,
            type: location.state.duration.type,
          });
          setPageVisible(res);
        }
      }
    };

    getReportDetails();
  }, []);

  const setPageVisible = (res) => {
    const is48h = res.data.req_type === "RPT_48H" || res.data.measurement_duration === "48 Hours";
    const isCanada = isCanadaReport(res.data);

    let lastPage = PAGE_CHART;

    if (is48h) {
      lastPage = PAGE_RADON_TABLE;

      if (isCanada) {
        // CA format always keeps Measurement Environments/Comments page.
        setPageAdditional(true);
        setPageRecommendations(false);
        setPageUncertainty(false);
        lastPage = PAGE_ADDITIONAL;
      } else {
        if (res.data.aux_images === undefined && res.data.comments === undefined) {
          setPageAdditional(false);
        } else {
          if (res.data.aux_images.length === 0 && res.data.comments === "") {
            setPageAdditional(false);
          } else {
            setPageAdditional(true);
            lastPage = PAGE_ADDITIONAL;
          }
        }

        if (res.data["ansi-aarst"] === undefined || res.data["ansi-aarst"] === 0) {
          setPageRecommendations(false);
        } else {
          setPageRecommendations(true);
          lastPage = PAGE_RECOMMENDATIONS;
        }

        setPageUncertainty(true);
        lastPage = PAGE_UNCERTAINTY;
      }
    }

    setLastPageIndex(lastPage);
  };

  const pageBefore = () => {
    if (pageIndex > 0) {
      let prev = pageIndex - 1;
      while (prev > PAGE_RADON_TABLE) {
        if (prev === PAGE_RECOMMENDATIONS && pageRecommendations) break;
        if (prev === PAGE_ADDITIONAL && pageAdditional) break;
        prev--;
      }
      setPageIndex(prev);
    }
  };

  const pageNext = () => {
    if (pageIndex < lastPageIndex) {
      let next = pageIndex + 1;
      while (next < lastPageIndex) {
        if (next <= PAGE_RADON_TABLE) break;
        if (next === PAGE_ADDITIONAL && pageAdditional) break;
        if (next === PAGE_RECOMMENDATIONS && pageRecommendations) break;
        next++;
      }
      setPageIndex(next);
    }
  };

  const handleClose = () => {
    setShowNoData(false);
    history.push({ pathname: "/reports/list" });
  };

  const waitForPdfImages = async (rootElement) => {
    if (!rootElement) return;
    const images = Array.from(rootElement.querySelectorAll("img"));
    if (images.length === 0) return;

    await Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            const isRenderable = () =>
              img.complete &&
              img.naturalWidth > 0 &&
              img.naturalHeight > 0;

            if (isRenderable()) {
              resolve();
              return;
            }
            const cleanupAndResolve = () => {
              clearTimeout(timeout);
              img.removeEventListener("load", handleSettled);
              img.removeEventListener("error", handleSettled);
              resolve();
            };
            const handleSettled = () => {
              // Error fallback image may load right after error event.
              requestAnimationFrame(cleanupAndResolve);
            };
            const timeout = setTimeout(cleanupAndResolve, 3000);
            img.addEventListener("load", handleSettled);
            img.addEventListener("error", handleSettled);
          })
      )
    );
  };

  const convertPDF = async (filename) => {
    ensurePdfFonts();
    let pdfRootHtmlElement = document.getElementById("pdfRoot");
    await waitForPdfImages(pdfRootHtmlElement);
    await new Promise((resolve) => setTimeout(resolve, 150));
    let res = drawDOM(pdfRootHtmlElement, {
      scale: 1,
      paperSize: "Letter",
      fileName: filename,
      margin: 0,
    })
      .then((group) => {
        return exportPDF(group);
      })
      .then((dataUri) => {
        let encoded = dataUri.split(";base64,")[1];
        return encoded;
      });

    return res;
  };

  const handleSave = async () => {
    const fileName =
      reportDetails.serial_number +
      "_" +
      reportDetails.client_name +
      "_" +
      reportDuration.start +
      "_" +
      reportDuration.end +
      ".pdf";

    let pdfRootHtmlElement = document.getElementById("pdfRoot");

    try {
      const result = await convertPDF(fileName);

      const controller = new AbortController();
      let token = await AuthUtil.getAuthToken();
      let user = await Auth.currentAuthenticatedUser();
      const clientName =
        reportDetails.client?.client_name ?? reportDetails.client_name;
      const clientDisplay =
        reportDetails.client?.client_display ??
        reportDetails.client_display ??
        reportDetails.client_email;
      const clientPhone =
        reportDetails.client?.client_phone ?? reportDetails.client_phone;

      let requestBody = {
        device_name: reportDetails.device_name,
        email: reportDetails.email,
        serial_number: reportDetails.serial_number,
        filename: fileName,
        client_name: clientName,
        client: {
          client_name: clientName,
          client_display: clientDisplay,
          client_phone: clientPhone,
        },
        address: reportDetails.address,
        activated: reportDetails.activated,
        deactivated: reportDetails.deactivated,
        device_location: reportDetails.device_location,
        device_placement: reportDetails.device_placement,
        calibration_date: reportDetails.calibration_date,
        measurement_start: reportDuration.start,
        measurement_end: reportDuration.end,
        measurement_unit: reportDetails.measurement_unit,
        company_logo_url: reportDetails.company_logo_url,
        filebody: result,
        radondata: reportDetails.radondata,
        highest_radon: reportDetails.highest_radon,
        lowest_radon: reportDetails.lowest_radon,
        avg_radon: reportDetails.avg_radon,
        generated_date: skipFlag ? originalGenerated : moment(reportDetails.generated_date).format("MM/DD/YYYY"),
        owner: {
          ownerDisplay: _.get(user, "attributes.custom:ownerDisplay"),
          certNo: _.get(user, "attributes.custom:certNo"),
          name: user.attributes.name,
        },
        measurement_duration: reportDetails.measurement_duration,
        req_type: reportDetails.req_type,
      };
      if (reportDetails.aux_images) requestBody.aux_images = reportDetails.aux_images;
      if (reportDetails.comments) requestBody.comments = reportDetails.comments;
      if (reportDetails["ansi-aarst"]) requestBody["ansi-aarst"] = reportDetails["ansi-aarst"];

      // if(!skipFlag) {
      //   requestBody['generated_date'] = moment(reportDetails.generated_date).format("MM/DD/YYYY");
      // } else {
      //   requestBody['generated_date'] = originalGenerated;
      // }

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        signal: controller.signal,
        credentials: "include",
        body: JSON.stringify(requestBody),
      };
      let requestUrl = apiEndPoint + "/report";
      const timeoutId = setTimeout(
        () => controller.abort(),
        CRITICAL_ACTION_TIMEOUT
      );
      let fetchRes = await fetch(requestUrl, requestOptions);
      let uploadResult = await fetchRes.json();

      if (uploadResult.resultcode >= 200 && uploadResult.resultcode < 300) {
        ensurePdfFonts();
        window.open(uploadResult.data.pdf_url);
        history.push("/reports/list");
        document.body.appendChild(pdfRootHtmlElement);
        savePDF(pdfRootHtmlElement, {
          scale: 1,
          paperSize: "Letter",
          fileName,
          margin: 0,
        }, () => document.body.removeChild(pdfRootHtmlElement));
      } else {
        throw Error("failed upload the generated report");
      }
    } catch (e) {
      alert(e);
    }
  };

  const getNoDataDialog = () => {
    return (
      <>
        <Dialog
          titleStyle={{ textAlign: "center" }}
          maxWidth="md"
          maxHeight={"sm"}
          open={showNoData}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
        >
          <div className="s5"></div>
          <DialogTitle id="alert-dialog-title">Information Message</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              There is no radon data collected during the period.
            </DialogContentText>
          </DialogContent>
          <div className="s10"></div>
          <DialogActions style={{ paddingRight: "20px" }}>
            <Button
              style={{
                background: "#E0E0E0",
                color: "#000000",
                width: "100px",
                height: "36px",
              }}
              onClick={handleClose}
            >
              OK
            </Button>
          </DialogActions>
          <div className="s10"></div>
        </Dialog>
      </>
    );
  };

  return (
    <>
      <Helmet title="Report Details" />
      <Typography variant="h3" gutterBottom display="inline">
        Radon Report
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/reports/list">
          Radon Reports
        </Link>
        <Typography>Details</Typography>
      </Breadcrumbs>
      <Divider my={6} />
      <>
        {!reportDuration.start ? (
          <Loading></Loading>
        ) : !reportDetails ? (
          getNoDataDialog()
        ) : (
          <Container>
            <ForPdf
              pdfExportComponent={pdfExportComponent}
              reportDetails={reportDetails}
              reportChartRadonValue={reportChartRadonValue}
              pageAdditional={pageAdditional}
              pageRecommendations={pageRecommendations}
              pageUncertainty={pageUncertainty}
            />

            <Grid
              style={{
                marginTop: 20,
                flexDirection: "row",
                width: "750px"
              }}
              justify="center"
              container
            >
              <Grid
                item
                xs={1}
                container
                alignItems="center"
                justify="center"
              >
                {pageIndex > 0 && (
                  <IconButton size="small" onClick={pageBefore}>
                    <ArrowBackIos style={{ color: "#8F95B2" }} />
                  </IconButton>
                )}
              </Grid>
              <Grid item xs={10}>
                <ReportPreview
                  page={pageIndex}
                  reportDetails={reportDetails}
                  reportChartRadonValue={reportChartRadonValue}
                  pageAdditional={pageAdditional}
                  pageRecommendations={pageRecommendations}
                  pageUncertainty={pageUncertainty}
                />
              </Grid>
              <Grid
                item
                xs={1}
                container
                alignItems="center"
                justify="center"
              >
                {pageIndex < lastPageIndex && (
                  <IconButton size="small" onClick={pageNext}>
                    <ArrowForwardIos style={{ color: "#8F95B2" }} />
                  </IconButton>
                )}
              </Grid>
            </Grid>
            <Grid
              style={{
                marginTop: 20,
                flexDirection: "row",
                width: "750px"
              }}
              justify="center"
              container
            >
              {location.state.duration.type === "48HR" ? (
                <Grid
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                  item
                >
                  <Button
                    onClick={handleClose}
                    style={{ width: "300px", marginTop: 0, marginBottom: 30 }}
                    size="large"
                    variant="contained"
                  >
                    Cancel
                  </Button>
                  <Box style={{ width: "10px" }} />
                  <Button
                    onClick={handleSave}
                    style={{ width: "300px", marginTop: 0, marginBottom: 30 }}
                    size="large"
                    variant="contained"
                  >
                    Save and Download
                  </Button>
                </Grid>
              ) : (
                <Grid item>
                  <Button
                    alignItems="center"
                    justify="center"
                    onClick={handleSave}
                    style={{ width: "300px", marginTop: 0, marginBottom: 30 }}
                    size="large"
                    variant="contained"
                  >
                    Save and Download
                  </Button>
                </Grid>
              )}
            </Grid>
          </Container>
        )}
      </>
    </>
  );
}

export default ReportDetails;
